'use client'

import { cn } from '@gyym/ui'
import Link from 'next/link'
import { useId, useState } from 'react'

import { isPagesRouterPath } from '@/utils/isPagesRouterPath'

import { type Faq, FAQS } from '../_data'
import { FadeIn } from './FadeIn'
import { ArrowDropUpIcon } from './icons'

const FaqItem = ({ faq }: { faq: Faq }) => {
  const [open, setOpen] = useState(false)
  const contentId = useId()

  return (
    <div className="relative z-0 flex w-full max-w-[90%] flex-col items-start max-[540px]:max-w-full">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left max-[540px]:pb-[10px]"
      >
        <span className="flex items-center justify-center">
          <span className="flex h-[33px] flex-col items-center">
            <span className="font-josefin-sans text-[22px] font-normal leading-[22px] text-black max-[540px]:text-[20px] max-[540px]:leading-5">
              #
            </span>
          </span>
          <span className="mr-[5px] font-roboto-condensed text-[40px] font-medium leading-[56px] tracking-[-2px] text-black max-[540px]:text-[36px] max-[540px]:leading-[50.4px] max-[540px]:tracking-[-1.8px]">
            {faq.number}
          </span>
          <span className="ml-2 text-[20px] font-normal leading-10 tracking-[1px] text-black max-[540px]:ml-[7px] max-[540px]:text-[16px] max-[540px]:leading-6 max-[540px]:tracking-[0.8px]">
            {faq.question}
          </span>
        </span>
        <span className="ml-2 flex size-[30px] shrink-0 items-center justify-center rounded-full bg-[#FF3194]">
          <ArrowDropUpIcon
            className={cn(
              'size-7 text-white transition-transform duration-300',
              !open && 'rotate-180',
            )}
          />
        </span>
      </button>
      {/* 番号の下のピンクのアンダーライン */}
      <div className="absolute left-[11px] top-[38px] z-[-1] h-[6px] w-[37px] bg-[#FF3194] max-[540px]:left-[9px] max-[540px]:top-[33px]" />
      <div className="h-px w-full bg-black" />
      <div
        id={contentId}
        aria-hidden={!open}
        className={cn(
          'grid w-full transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.4,0.4,0,1)]',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex w-full flex-col items-center">
            <p className="my-[10px] w-full px-[15px] text-[15px] font-normal leading-[30px] tracking-[0.75px] max-[540px]:my-[15px] max-[540px]:pl-[10px] max-[540px]:pr-0 max-[540px]:text-[14px] max-[540px]:leading-[23.8px] max-[540px]:tracking-[0.7px]">
              {faq.answer.map((line, i) => (
                <span key={line}>
                  {i > 0 && <br />}
                  {line}
                </span>
              ))}
            </p>
            {faq.link && (
              <Link
                href={faq.link.href}
                prefetch={isPagesRouterPath(faq.link.href) ? false : undefined}
                tabIndex={open ? undefined : -1}
                className="flex h-10 w-[200px] items-center justify-center rounded-[64px] bg-[#001E62] text-[18px] font-bold leading-[25.2px] text-white max-[540px]:mx-[30px] max-[540px]:h-[50px] max-[540px]:w-[170px] max-[540px]:max-w-[calc(100%-60px)] max-[540px]:text-[16px] max-[540px]:leading-[22.4px]"
              >
                {faq.link.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/** よくある質問 */
export const FaqSection = () => (
  <section className="flex flex-col items-center gap-5 bg-[#EEEEEE] pb-[30px] pt-[70px] max-[540px]:pt-10">
    <FadeIn delay={600} duration={1000} translateY={15}>
      <p className="text-center text-[48px] font-bold leading-[67.2px] tracking-[4.8px] max-[820px]:text-[32px] max-[820px]:leading-[44.8px] max-[820px]:tracking-[3.2px] max-[540px]:text-[28px] max-[540px]:leading-[39.2px] max-[540px]:tracking-[1.4px]">
        よくある質問
      </p>
    </FadeIn>
    <p className="text-center text-[15px] font-normal leading-[25.5px] tracking-[0.75px] max-[820px]:text-[14px] max-[820px]:leading-[23.8px] max-[820px]:tracking-[0.7px] max-[540px]:max-w-[80%]">
      ご担当者の皆様から多く寄せられる質問をまとめました。
      <br className="max-[540px]:hidden" />
      他にもご不明な点がございましたら、お気軽にお問い合わせください。
    </p>
    <div className="flex w-[800px] max-w-full flex-col items-center gap-5 px-10 py-[52px] max-[820px]:max-w-[95%] max-[820px]:p-10 max-[540px]:max-w-[90%] max-[540px]:items-start max-[540px]:px-0 max-[540px]:py-[30px]">
      {FAQS.map((faq) => (
        <FadeIn
          key={faq.number}
          delay={400}
          className="flex w-full flex-col items-center max-[540px]:items-start"
        >
          <FaqItem faq={faq} />
        </FadeIn>
      ))}
    </div>
  </section>
)
